import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import EventHubDialog, {
    EVENT_HUB_DIALOG_BUTTON_CLASS,
    EVENT_HUB_DIALOG_DANGER_CLASS,
    EVENT_HUB_DIALOG_GHOST_CLASS,
    EVENT_HUB_DIALOG_PRIMARY_CLASS,
    EVENT_HUB_DIALOG_Z,
    EventHubDialogActions,
} from '@/components/EventHub/EventHubDialog'

const DANGER = 'danger'

/**
 * Click-to-confirm wrapper: `children` is the trigger, the question opens in the kit confirm dialog
 * (EventHubDialog, same look as EventHubConfirmDialog). Same API as before: `type` 'danger' (red
 * action) or anything else (ink action), `needConfirm` false = run at once, `isInfoOnly` = a single
 * "Close", `conditionalConfirm` = close only when `onConfirm()` returns true, `doubleConfirmation` =
 * a checkbox gating the action. Clicks are stopped so a clickable row behind never fires.
 */
export default function Confirm({
    onConfirm,
    onCancel = () => {},
    title,
    message,
    children,
    type = DANGER,
    className,
    style,
    needConfirm = true,
    isInfoOnly = false,
    conditionalConfirm = false,
    doubleConfirmation = false,
    doubleConfirmationMessage = 'I confirm',
}) {
    const { t } = useTranslation()

    const [isOpen, setIsOpen] = useState(false)
    const [acceptDoubleConfirmation, setAcceptDoubleConfirmation] = useState(false)
    const cancelRef = useRef(null)

    const stop = (event) => event.stopPropagation()

    const HandleOpenClick = (event) => {
        event.stopPropagation()
        if (needConfirm) {
            setIsOpen(true)
            setAcceptDoubleConfirmation(false)
        } else {
            onConfirm()
        }
    }

    const HandleConfirmClick = () => {
        if (!conditionalConfirm) {
            setIsOpen(false)
            onConfirm()
        } else if (onConfirm()) {
            setIsOpen(false)
        }
    }

    const HandleCancelClick = () => {
        setIsOpen(false)
        onCancel()
    }

    return (
        <>
            {isOpen && (
                <div onClick={stop} onKeyDown={stop}>
                    <EventHubDialog
                        id="model-confirm"
                        title={title}
                        zClass={EVENT_HUB_DIALOG_Z.CONFIRM}
                        width="sm"
                        closable={false}
                        initialFocusRef={cancelRef}
                        onClose={HandleCancelClick}
                    >
                        {message ? (
                            <div className="m-0 flex flex-col gap-3 text-sm leading-[1.45] text-[#5b5f6e]">{message}</div>
                        ) : null}
                        {doubleConfirmation && (
                            <label className="flex cursor-pointer items-center gap-2 text-sm text-[#272727]">
                                <input
                                    type="checkbox"
                                    className="size-4 cursor-pointer accent-[#272727]"
                                    checked={acceptDoubleConfirmation}
                                    onChange={(event) => setAcceptDoubleConfirmation(event.target.checked)}
                                />
                                {doubleConfirmationMessage}
                            </label>
                        )}
                        <EventHubDialogActions single={isInfoOnly}>
                            <button
                                ref={cancelRef}
                                type="button"
                                onClick={HandleCancelClick}
                                className={`${EVENT_HUB_DIALOG_BUTTON_CLASS} ${EVENT_HUB_DIALOG_GHOST_CLASS}`}
                            >
                                {isInfoOnly ? t('Close') : t('Cancel')}
                            </button>
                            {!isInfoOnly && (
                                <button
                                    type="button"
                                    onClick={HandleConfirmClick}
                                    disabled={doubleConfirmation && !acceptDoubleConfirmation}
                                    className={`${EVENT_HUB_DIALOG_BUTTON_CLASS} ${
                                        type === DANGER ? EVENT_HUB_DIALOG_DANGER_CLASS : EVENT_HUB_DIALOG_PRIMARY_CLASS
                                    }`}
                                >
                                    {t('Confirm')}
                                </button>
                            )}
                        </EventHubDialogActions>
                    </EventHubDialog>
                </div>
            )}
            <div onClick={HandleOpenClick} className={className} style={style}>
                {children}
            </div>
        </>
    )
}
